import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation, MapPin, Droplets, Flag, Home, CheckCircle2, Circle, Wifi, WifiOff, RefreshCw, AlertTriangle, LogOut, Plus, ClipboardCheck, Phone, Info, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { PhoneSetupGuide, phoneSetupDone, isStandalone } from "@/components/PhoneSetupGuide";
import DriverNav from "@/components/DriverNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isFleet, unitLabel } from "@/lib/industry";
import { useOffline, getQueue } from "@/lib/offline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad, PreStartModal } from "@/pages/DriverPortal";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { DocketQR } from "@/components/DocketQR";
import { speakDriveSafe, tone, newLoadBell } from "@/lib/voice";
import { pushSupported, currentPushSubscription, subscribeToPush } from "@/lib/push";

const STEPS = [
  { key: "en_route", label: "Depart Plant", sub: "Leaving the batch plant", icon: Navigation, from: ["loaded", "slumping"] },
  { key: "on_site", label: "Arrived On Site", sub: "At the customer's site", icon: MapPin, from: ["en_route"] },
  { key: "discharging", label: "Start Discharge", sub: "Pouring has started", icon: Droplets, from: ["on_site"] },
  { key: "sign", label: "Finish · Docket Signed", sub: "Customer signs the docket", icon: Flag, from: ["discharging", "on_site"] },
  { key: "returning", label: "Returning To Plant", sub: "Docket complete", icon: Home, from: [] },
];
// Fleet Dispatch drivers haul & tip product — no plant/pour/discharge language.
const FLEET_STEPS = [
  { key: "en_route", label: "Depart Depot", sub: "Leaving the depot", icon: Navigation, from: ["loaded", "slumping"] },
  { key: "on_site", label: "Arrived On Site", sub: "At the delivery site", icon: MapPin, from: ["en_route"] },
  { key: "discharging", label: "Start Unload", sub: "Unloading has started", icon: Droplets, from: ["on_site"] },
  { key: "sign", label: "Finish · Docket Signed", sub: "Customer signs the docket", icon: Flag, from: ["discharging", "on_site"] },
  { key: "returning", label: "Returning To Depot", sub: "Delivery complete", icon: Home, from: [] },
];
const ORDER = ["loaded", "slumping", "en_route", "on_site", "discharging", "delivered"];
const STATUS_LABEL = { loaded: "LOADED · AT PLANT", slumping: "SLUMP CHECK", en_route: "EN ROUTE", on_site: "ON SITE", discharging: "DISCHARGING", delivered: "DELIVERED" };
const FLEET_STATUS_LABEL = { loaded: "LOADED · AT DEPOT", slumping: "LOADED", en_route: "EN ROUTE", on_site: "ON SITE", discharging: "UNLOADING", delivered: "DELIVERED" };

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(d));

function wcOf(d) {
  const cem = Number(d.mix_cement_kg || 0), wat = Number(d.mix_water_kg || 0), qty = Number(d.quantity_m3 || 0);
  const added = Number(d.plant_water_l || 0) + Number(d.slump_water_l || 0) + Number(d.site_water_adjust_l || 0);
  const design = d.wc_design ?? (cem ? wat / cem : null);
  const actual = cem && qty ? (wat * qty + added) / (cem * qty) : d.wc_actual ?? null;
  const max = d.mix_max_wc ?? (cem ? (wat + 20) / cem : null);
  return { design, actual, max, added, over: actual !== null && max !== null && actual > max + 1e-9 };
}

export default function DriverApp() {
  const { user, org, logout } = useAuth();
  const fleet = isFleet(org);
  const steps = fleet ? FLEET_STEPS : STEPS;
  const statusLabel = fleet ? FLEET_STATUS_LABEL : STATUS_LABEL;
  const nav = useNavigate();
  const off = useOffline();
  const [data, setData] = useState({ trucks: [], dockets: [], assigned_jobs: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [water, setWater] = useState(null);
  const [signing, setSigning] = useState(null);
  const [slumpEdit, setSlumpEdit] = useState(null);
  const [preStart, setPreStart] = useState({ truck: null, submitted: false });
  const [showPreStart, setShowPreStart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState("idle");   // idle | requesting | tracking | denied | unsupported
  const [newLoad, setNewLoad] = useState(null);   // freshly-pinged docket awaiting the driver's ack
  const [pushAsk, setPushAsk] = useState(false);  // nudge to switch on lock-screen alerts
  const seenRef = useRef(null);                    // docket ids already known to this screen

  // Drivers work in sunlight — always light palette, brand accent still follows the plant's colour.
  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.removeAttribute("data-theme");
    return () => { if (prev) document.documentElement.setAttribute("data-theme", prev); };
  }, []);

  const applyPending = useCallback((d) => {
    const q = getQueue();
    if (!q.length || !d?.dockets) return d;
    let dockets = d.dockets;
    for (const it of q) {
      const m = (it.url || "").match(/^\/dockets\/([^/]+)\/(status|sign|water)$/);
      if (!m) continue;
      const [, id, kind] = m;
      let body = {}; try { body = JSON.parse(it.data || "{}") || {}; } catch { /* ignore */ }
      if (kind === "sign") dockets = dockets.filter((x) => x.id !== id);
      else dockets = dockets.map((x) => (x.id === id ? { ...x, ...(kind === "status" ? { status: body.status } : body) } : x));
    }
    return { ...d, dockets };
  }, []);
  const load = useCallback(() => api.get("/driver/my-dockets").then((r) => setData(applyPending(r.data))).catch(() => {}), [applyPending]);
  const loadPreStart = useCallback(() => api.get("/driver/pre-start/today").then((r) => { setPreStart(r.data); if (r.data.truck && !r.data.submitted) setShowPreStart(true); }).catch(() => {}), []);
  useEffect(() => { load(); loadPreStart(); const i = setInterval(() => { if (navigator.onLine) load(); }, 10000); return () => clearInterval(i); }, [load, loadPreStart]);
  useEffect(() => { const h = () => load(); window.addEventListener("ab-offline-synced", h); return () => window.removeEventListener("ab-offline-synced", h); }, [load]);

  const dockets = data.dockets || [];
  const docket = dockets.find((d) => d.id === selectedId) || dockets[0] || null;
  const truck = data.trucks?.[0];

  // ── WASH / DUMP INSTRUCTIONS ────────────────────────────────────────────────
  // Dispatch can ping Wash Out / Wash Fins / Dump Leftovers to the truck. Show the
  // un-acknowledged ones as a banner, ring the bell, and let the driver tick them off.
  const WASH_LABELS = { wash_out: "Wash Out", wash_fins: "Wash Fins", dump_leftovers: "Dump Leftovers" };
  const washTasks = Object.entries(truck?.wash_tasks || {})
    .filter(([, v]) => v && !v.acked_at)
    .map(([k]) => k);
  const washKey = washTasks.join(",");
  useEffect(() => {
    if (!washKey) return undefined;
    newLoadBell();
    const i = setInterval(newLoadBell, 5000);
    return () => clearInterval(i);
  }, [washKey]);
  const ackWash = async () => {
    try {
      await Promise.all(washTasks.map((task) => api.post("/driver/wash-task-ack", { task })));
      toast.success("Acknowledged — thanks");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't acknowledge"); }
  };

  // ── NEW LOAD BELL ──────────────────────────────────────────────────────────
  // Ring the moment a brand-new load is pinged to this truck. First poll primes
  // silently so loads already open when the screen opens don't re-fire.
  useEffect(() => {
    const ids = new Set(dockets.map((d) => d.id));
    if (seenRef.current === null) { seenRef.current = ids; return; }
    const fresh = dockets.find((d) => ["loaded", "slumping"].includes(d.status) && !seenRef.current.has(d.id));
    seenRef.current = ids;
    if (fresh) { setNewLoad(fresh); setSelectedId(fresh.id); }
  }, [data.dockets]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the alert once the driver has moved the load on (or it's gone).
  useEffect(() => {
    if (newLoad && !dockets.some((d) => d.id === newLoad.id && ["loaded", "slumping"].includes(d.status))) setNewLoad(null);
  }, [data.dockets]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Repeat the bell + buzz every few seconds until the driver acknowledges.
  useEffect(() => {
    if (!newLoad) return undefined;
    newLoadBell();
    const i = setInterval(newLoadBell, 4000);
    return () => clearInterval(i);
  }, [newLoad]);

  const ackNewLoad = () => { if (newLoad) { setSelectedId(newLoad.id); setNewLoad(null); } };

  // Make sure lock-screen push works: silently re-subscribe if already allowed,
  // otherwise nudge the driver to switch it on once.
  useEffect(() => {
    if (!pushSupported() || Notification.permission === "denied") return;
    currentPushSubscription().then((s) => {
      if (s) return;
      if (Notification.permission === "granted") subscribeToPush().catch(() => {});
      else setPushAsk(true);
    }).catch(() => {});
  }, []);
  const enablePush = async () => {
    try { await subscribeToPush(); setPushAsk(false); toast.success("Load alerts on — you'll be pinged even with the screen off"); }
    catch (e) { toast.error(e.message || "Couldn't turn on alerts"); }
  };

  const wc = docket ? wcOf(docket) : null;
  const stepIdx = docket ? ORDER.indexOf(docket.status) : -1;

  const setStatus = async (status) => {
    if (!docket || busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/dockets/${docket.id}/status`, { status }, { offline: `${docket.docket_no} · ${status.replace("_", " ")}` });
      setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === docket.id ? { ...x, status } : x)) }));
      if (!res.data?.queued) { toast.success(statusLabel[status] || status); load(); }
      if (status === "en_route") speakDriveSafe();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update — try again"); } finally { setBusy(false); }
  };

  // GPS-raised "Load delivered?" nag — beep once so it's noticed in a noisy cab.
  const promptedRef = useRef(null);
  useEffect(() => {
    if (!docket?.completion_prompt_at) return;
    if (promptedRef.current === docket.id) return;
    promptedRef.current = docket.id;
    tone(880, 0.2, 0, 0.5); tone(1100, 0.25, 0.22, 0.5);
    toast.warning("Looks like you've left the site — close the docket off", { duration: 20000 });
  }, [docket?.completion_prompt_at, docket?.id]);

  const dismissPrompt = async () => {
    if (!docket || busy) return;
    setBusy(true);
    try {
      await api.post(`/dockets/${docket.id}/dismiss-prompt`);
      setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === docket.id ? { ...x, completion_prompt_at: null } : x)) }));
      promptedRef.current = null;
    } catch { toast.error("Couldn't update — try again"); } finally { setBusy(false); }
  };

  // Live GPS → War Room / GPS map. Streams whenever the app is open and a truck is assigned:
  // every 8 s while a load is on the go, every 30 s while idle at the yard.
  const hasLiveLoad = (data.dockets || []).some((d) => ["loaded", "en_route", "on_site", "discharging"].includes(d.status));
  const activeTruckId = useMemo(() => {
    const live = (data.dockets || []).find((d) => ["loaded", "en_route", "on_site", "discharging"].includes(d.status));
    return live?.truck_id || (data.trucks?.[0] && data.trucks[0].id) || null;
  }, [data.dockets, data.trucks]);
  useEffect(() => {
    if (!activeTruckId) { setGps("idle"); return undefined; }
    if (!("geolocation" in navigator)) { setGps("unsupported"); return undefined; }
    setGps("requesting");
    let lastSent = 0, lastFix = 0, watchId = null;
    const gap = hasLiveLoad ? 8000 : 30000;
    const send = (pos, force = false) => {
      setGps("tracking");
      const now = Date.now();
      lastFix = now;
      if (!force && now - lastSent < gap) return;
      lastSent = now;
      const { latitude, longitude, heading, speed } = pos.coords;
      api.post(`/trucks/${activeTruckId}/location`, {
        lat: latitude, lng: longitude,
        heading: Number.isFinite(heading) ? heading : null,
        speed_kph: Number.isFinite(speed) ? speed * 3.6 : null,
        source: "driver_gps",
      }).then(() => setPingAt(Date.now())).catch(() => {});
    };
    const onErr = (err) => setGps(err?.code === 1 ? "denied" : "error");
    const opts = { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 };
    const startWatch = () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      watchId = navigator.geolocation.watchPosition((p) => send(p), onErr, opts);
    };
    startWatch();
    // Heartbeat: phones throttle watchPosition when the truck sits still or the screen
    // dims, and the board then flags the truck DARK. Force a fix + ping if we've had
    // nothing for a full gap, and restart the watch if it has gone quiet.
    const beat = setInterval(() => {
      const now = Date.now();
      if (now - lastSent < gap) return;
      navigator.geolocation.getCurrentPosition((p) => send(p, true), () => {}, { ...opts, maximumAge: 15000 });
      if (now - lastFix > gap * 3) startWatch();
    }, gap);
    // Coming back from the lock screen / another app: ping straight away and re-arm.
    const onVis = () => { if (document.visibilityState === "visible") { navigator.geolocation.getCurrentPosition((p) => send(p, true), () => {}, opts); startWatch(); api.post(`/trucks/${activeTruckId}/navigating`, { on: false }).catch(() => {}); } };
    kickRef.current = onVis;
    document.addEventListener("visibilitychange", onVis);
    return () => { kickRef.current = null; clearInterval(beat); document.removeEventListener("visibilitychange", onVis); if (watchId != null) navigator.geolocation.clearWatch(watchId); };
  }, [hasLiveLoad, activeTruckId]);

  // Seconds since the last ping actually reached dispatch — drives the big GPS strip.
  const [pingAt, setPingAt] = useState(0);
  const [tick, setTick] = useState(0);
  const kickRef = useRef(null);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const pingAge = pingAt ? Math.round((Date.now() - pingAt) / 1000) + tick * 0 : null;
  const gpsStale = hasLiveLoad && (pingAge == null || pingAge > 45);
  const [showSetup, setShowSetup] = useState(() => !phoneSetupDone());
  const [navOpen, setNavOpen] = useState(false);

  // Keep the screen awake on a live load — iOS/Android stop GPS the moment the phone locks.
  useEffect(() => {
    if (!hasLiveLoad) return undefined;
    let lock = null;
    const grab = async () => { try { lock = await navigator.wakeLock?.request("screen"); } catch { /* unsupported */ } };
    grab();
    const onVis = () => { if (document.visibilityState === "visible") grab(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); try { lock?.release(); } catch { /* ignore */ } };
  }, [hasLiveLoad]);

  const pendingHere = off.pending.length;
  const syncBadge = off.stuck
    ? { cls: "bg-red-600 text-white", icon: AlertTriangle, text: `SYNC STUCK · ${pendingHere}` }
    : !off.online ? { cls: "bg-amber-500 text-black", icon: WifiOff, text: `OFFLINE · ${pendingHere} SAVED` }
    : pendingHere ? { cls: "bg-amber-500 text-black", icon: RefreshCw, text: `SYNCING · ${pendingHere}` }
    : { cls: "bg-emerald-600 text-white", icon: Wifi, text: "LIVE · SYNCED" };
  const SyncIcon = syncBadge.icon;

  return (
    <div className="min-h-screen bg-white text-[#0f1419] max-w-md mx-auto flex flex-col" data-testid="driver-app">
      <ImpersonationBanner />
      {showSetup && <PhoneSetupGuide onDone={() => setShowSetup(false)} />}
      {navOpen && docket && (
        <DriverNav
          destination={docket.site_lat != null && docket.site_lng != null ? { lat: docket.site_lat, lng: docket.site_lng } : docket.site_address}
          label={docket.site_name || docket.site_address}
          onClose={() => setNavOpen(false)}
          onFallback={() => { if (activeTruckId) api.post(`/trucks/${activeTruckId}/navigating`, { on: true }).catch(() => {}); }}
        />
      )}
      <header className="px-4 pt-4 pb-3 border-b-2 border-[rgb(var(--primary))]/20 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-[rgb(var(--primary))] font-bold">{(org?.name || org?.brand_name || "ArmorBatch").toUpperCase()} DRIVER</div>
          <div className="heading text-lg font-black truncate" data-testid="driver-app-name">{user?.name}{truck ? ` · ${truck.number}` : ""}</div>
        </div>
        <button type="button" onClick={() => off.drain()} data-testid="driver-app-sync" className={`shrink-0 inline-flex items-center gap-1.5 mono text-[10px] font-bold tracking-widest px-2.5 py-1.5 rounded-full ${syncBadge.cls}`}>
          <SyncIcon className={`w-3.5 h-3.5 ${off.syncing ? "animate-spin" : ""}`} /> {syncBadge.text}
          {gps === "tracking" && <span className="inline-flex items-center gap-0.5 ml-1 pl-1.5 border-l border-white/40" data-testid="driver-app-gps-live"><Navigation className="w-3 h-3" /> GPS</span>}
        </button>
      </header>
      {newLoad && (
        <button type="button" onClick={ackNewLoad} data-testid="driver-app-new-load-alert"
          className="w-full px-4 py-4 text-left flex items-center gap-3 border-b-2 border-black/10 bg-[rgb(var(--primary))] text-white animate-pulse">
          <BellRing className="w-9 h-9 shrink-0 animate-bounce" />
          <div className="min-w-0 flex-1">
            <div className="heading text-lg font-black uppercase leading-tight" data-testid="driver-app-new-load-title">New load · {newLoad.truck_number || truck?.number || ""}</div>
            <div className="mono text-[12px] opacity-95 truncate">Docket {newLoad.docket_no} · {newLoad.customer_name} · {fmt(newLoad.quantity_m3, 1)} {unitLabel(newLoad.unit)} {newLoad.mix_code}</div>
            <div className="mono text-[11px] opacity-80 mt-0.5 uppercase tracking-widest">Tap to view &amp; silence</div>
          </div>
        </button>
      )}
      {!fleet && washTasks.length > 0 && (
        <div className="w-full px-4 py-3 border-b-2 border-black/10 bg-sky-600 text-white" data-testid="driver-wash-alert">
          <div className="flex items-center gap-3">
            <Droplets className="w-7 h-7 shrink-0 animate-bounce" />
            <div className="min-w-0 flex-1">
              <div className="heading text-base font-black uppercase leading-tight">Yard jobs from dispatch</div>
              <div className="mono text-[12px] opacity-95">{washTasks.map((k) => WASH_LABELS[k]).join(" · ")}</div>
            </div>
            <button type="button" onClick={ackWash} data-testid="driver-wash-ack"
              className="shrink-0 bg-white text-sky-700 font-black uppercase text-xs px-3 py-2 rounded">Got it ✓</button>
          </div>
        </div>
      )}
      {pushAsk && !newLoad && (
        <button type="button" onClick={enablePush} data-testid="driver-app-enable-alerts"
          className="w-full bg-sky-600 text-white px-4 py-2.5 text-xs font-bold text-left border-b border-sky-700 flex items-center gap-2">
          <Bell className="w-4 h-4 shrink-0" /> Turn on load alerts — get a bell + popup even when your phone is locked. Tap to allow.
        </button>
      )}
      {off.stuck && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-xs text-red-700 flex items-center justify-between gap-2" data-testid="driver-app-stuck">
          <span>{pendingHere} action{pendingHere === 1 ? "" : "s"} not reaching dispatch — {off.lastError || "server unreachable"}.</span>
          <button type="button" onClick={() => off.drain()} className="font-bold uppercase mono text-[10px] underline">Retry</button>
        </div>
      )}
      {hasLiveLoad && gps !== "denied" && gps !== "unsupported" && (
        <button type="button" onClick={() => kickRef.current?.()} data-testid="driver-app-gps-strip"
          className={`w-full px-4 py-3 text-left flex items-center gap-3 border-b-2 ${gpsStale ? "bg-red-600 border-red-700 text-white animate-pulse" : "bg-emerald-600 border-emerald-700 text-white"}`}>
          <Navigation className="w-6 h-6 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="heading text-base font-black uppercase leading-tight">{gpsStale ? "GPS DARK — dispatch can't see you" : "GPS live — dispatch can see you"}</div>
            <div className="mono text-[11px] opacity-90">{gpsStale ? "Tap here · keep this screen ON and this app in front" : `last ping ${pingAge}s ago · keep this screen on`}</div>
          </div>
        </button>
      )}
      {gps !== "tracking" && gps !== "idle" && (
        <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b ${gps === "requesting" ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="driver-app-gps-status">
          <Navigation className={`w-4 h-4 shrink-0 ${gps === "requesting" ? "animate-pulse" : ""}`} />
          {gps === "requesting" && "Waiting for GPS fix — allow location when your phone asks"}
          {gps === "denied" && "Location blocked — dispatch can't see your truck. Enable Location for this site in your phone's browser settings."}
          {gps === "unsupported" && "This browser has no GPS support — dispatch can't see your truck."}
          {gps === "error" && "GPS signal lost — will retry automatically."}
        </div>
      )}
      {!showSetup && !isStandalone() && hasLiveLoad && (
        <button type="button" onClick={() => setShowSetup(true)} data-testid="driver-app-install-nudge" className="w-full bg-amber-400 text-black px-4 py-2 text-xs font-bold text-left border-b border-amber-500">
          Running in the browser — install the app to your home screen so GPS doesn't drop. Tap for steps.
        </button>
      )}
      {preStart.truck && !preStart.submitted && !showPreStart && (
        <button type="button" onClick={() => setShowPreStart(true)} data-testid="driver-app-prestart-banner" className="mx-4 mt-3 flex items-center gap-2 border-2 border-amber-400 bg-amber-50 text-amber-900 px-3 py-3 text-sm font-bold text-left">
          <ClipboardCheck className="w-5 h-5 shrink-0" /> Pre-start checklist not done today — tap to complete before departing
        </button>
      )}

      <main className="flex-1 px-4 py-4 space-y-4">
        {dockets.length > 1 && (
          <div className="flex gap-2 overflow-x-auto" data-testid="driver-app-docket-tabs">
            {dockets.map((d) => (
              <button key={d.id} type="button" onClick={() => setSelectedId(d.id)} className={`mono text-xs px-3 py-2 border-2 whitespace-nowrap ${d.id === docket?.id ? "border-[rgb(var(--primary))] text-[rgb(var(--primary))] font-bold" : "border-gray-200 text-gray-500"}`}>{d.docket_no}</button>
            ))}
          </div>
        )}

        {!docket && (
          <div className="border-2 border-dashed border-gray-200 p-8 text-center" data-testid="driver-app-empty">
            <div className="heading text-2xl font-black text-gray-300">NO LOAD</div>
            <div className="text-sm text-gray-500 mt-2">You'll see the docket here as soon as the batcher loads your truck.</div>
            {(data.assigned_jobs || []).length > 0 && (
              <div className="mt-4 text-left space-y-2">
                <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Next up</div>
                {data.assigned_jobs.map((j) => (
                  <div key={j.id} className="border border-gray-200 p-3 text-sm"><b>{j.customer_name}</b> · {j.mix_code} · {j.quantity_m3} {unitLabel(j.unit)}<div className="text-xs text-gray-500">{j.site_address || j.site_name} · {j.time_required || ""}</div>
                    {j.driver_instructions && (
                      <div className="mt-2 border-l-4 border-amber-400 bg-amber-50 px-2 py-1.5" data-testid={`driver-app-job-instructions-${j.id}`}>
                        <div className="mono text-[9px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1"><Info className="w-3 h-3" /> Site instructions</div>
                        <div className="text-xs text-gray-800 mt-0.5 whitespace-pre-wrap">{j.driver_instructions}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {docket && (
          <>
            <section className="border-2 border-gray-100 p-4" data-testid="driver-app-load">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Docket {docket.docket_no}</div>
                  <div className="heading text-xl font-black truncate" data-testid="driver-app-customer">{docket.customer_name}</div>
                  <div className="text-sm text-gray-600 truncate">{docket.site_address || docket.site_name}</div>
                  {docket.job_load_seq > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="driver-app-load-seq">
                      <span className="mono text-[11px] font-bold px-2 py-1 bg-[rgb(var(--primary))] text-white">LOAD {docket.job_load_seq}</span>
                      <span className="mono text-[11px] px-2 py-1 border-2 border-gray-200 text-gray-700" data-testid="driver-app-job-progress">
                        {fmt(docket.job_progress_m3, 1)}{docket.job_total_m3 > 0 ? ` / ${fmt(docket.job_total_m3, 1)}` : ""} {unitLabel(docket.unit)} {fleet ? "on this delivery" : "on this pour"}
                      </span>
                      <span className="mono text-[11px] px-2 py-1 border-2 border-gray-200 text-gray-500" data-testid="driver-app-day-total">
                        Your day · load {docket.day_load_seq} · {fmt(docket.day_total_m3, 1)} {unitLabel(docket.unit)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="heading text-3xl font-black text-[rgb(var(--primary))]">{fmt(docket.quantity_m3, 1)}<span className="text-sm ml-0.5">{unitLabel(docket.unit)}</span></div>
                  <div className="mono text-[10px] uppercase text-gray-500">{docket.mix_code}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <div className="flex-1 [&>button]:w-full [&>button]:justify-center [&>button]:py-3 [&>button]:text-sm [&>button]:font-bold [&>button]:border-2 [&>button]:border-gray-200 [&>button]:text-inherit" data-testid="driver-app-qr">
                  <DocketQR docket={docket} size="lg" label="Show QR" />
                </div>
                {docket.site_contact_phone && <a href={`tel:${docket.site_contact_phone}`} className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-gray-200 py-3 text-sm font-bold" data-testid="driver-app-call"><Phone className="w-4 h-4" /> Call site</a>}
                {(docket.site_address) && <button type="button" onClick={() => setNavOpen(true)}
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-gray-200 py-3 text-sm font-bold" data-testid="driver-app-directions"><Navigation className="w-4 h-4" /> Navigate</button>}
              </div>
            </section>

            {docket.pump_unit && (
              <section className="border-2 border-sky-400 bg-sky-50 p-4" data-testid="driver-app-pump-crew">
                <div className="mono text-[10px] uppercase tracking-widest text-sky-700 font-black">Pump on site</div>
                <div className="font-black text-lg text-gray-900 mt-0.5">{docket.pump_unit.company} · {docket.pump_unit.name}</div>
                <div className="text-sm text-gray-700">{docket.pump_unit.operator_name || "Operator TBA"}</div>
                {docket.pump_unit.phone && (
                  <a href={`tel:${docket.pump_unit.phone}`} className="mt-2 inline-flex items-center justify-center gap-2 border-2 border-sky-300 bg-white px-4 py-2 text-sm font-bold" data-testid="driver-app-call-pump">
                    <Phone className="w-4 h-4" /> Call pump {docket.pump_unit.phone}
                  </a>
                )}
              </section>
            )}

            {docket.driver_instructions && (
              <section className="border-2 border-amber-400 bg-amber-50 p-4" data-testid="driver-app-instructions">
                <div className="mono text-[10px] uppercase tracking-widest text-amber-700 font-black flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" /> Site instructions
                </div>
                <div className="text-base text-gray-900 mt-1.5 leading-relaxed whitespace-pre-wrap" data-testid="driver-app-instructions-text">{docket.driver_instructions}</div>
              </section>
            )}

            {!fleet && <SlumpCard docket={docket} onSaved={(patch) => setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === docket.id ? { ...x, ...patch } : x)) }))} />}

            {!fleet && (
            <section className={`border-2 p-4 ${wc?.over ? "border-red-500 bg-red-50" : "border-gray-100"}`} data-testid="driver-app-wc">
              <div className="flex items-center justify-between">
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1"><Droplets className="w-3 h-3" /> Water · W/C ratio</div>
                  <div className="text-sm text-gray-600 mt-1">Added on site: <b data-testid="driver-app-water-added">{fmt(wc.added, 0)} L</b> · design {fmt(wc.design)} · max {fmt(wc.max)}</div>
                </div>
                <div className="text-right">
                  <div className={`heading text-3xl font-black ${wc.over ? "text-red-600" : "text-emerald-600"}`} data-testid="driver-app-wc-actual">{fmt(wc.actual)}</div>
                  <div className={`mono text-[10px] uppercase font-bold ${wc.over ? "text-red-600" : "text-emerald-600"}`}>{wc.actual === null ? "no mix data" : wc.over ? "OVER MAX" : "W/C OK"}</div>
                </div>
              </div>
              {wc.over && <div className="mt-2 text-xs text-red-700 font-bold flex items-center gap-1" data-testid="driver-app-wc-warning"><AlertTriangle className="w-3.5 h-3.5" /> Exceeds the mix's max W/C — customer must accept responsibility (noted on docket).</div>}
              <button type="button" onClick={() => setWater(docket)} disabled={stepIdx >= ORDER.indexOf("delivered")} data-testid="driver-app-add-water" className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-[rgb(var(--primary))] text-[rgb(var(--primary))] py-3 font-bold uppercase mono text-sm disabled:opacity-40">
                <Plus className="w-4 h-4" /> Add water
              </button>
            </section>
            )}

            {/* GPS says this truck left the site after pouring — one big tap to
                close the load out, instead of hunting through the steps. */}
            {docket.completion_prompt_at && (
              <section className="border-4 border-[rgb(var(--primary))] bg-[rgb(var(--primary))]/10 p-4" data-testid="driver-app-completion-prompt">
                <div className="mono text-[10px] uppercase tracking-widest text-[rgb(var(--primary))] font-black flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> You've left the site
                </div>
                <div className="heading text-2xl font-black text-gray-900 mt-1 leading-tight">Load delivered?</div>
                <div className="text-sm text-gray-700 mt-1">
                  Docket {docket.docket_no} is still open. Close it off so dispatch can send you the next load.
                </div>
                <button type="button" onClick={() => setSigning(docket)} data-testid="driver-app-prompt-yes"
                  className="mt-3 w-full bg-emerald-600 text-white py-5 heading text-2xl font-black uppercase active:bg-emerald-700 flex items-center justify-center gap-3">
                  <CheckCircle2 className="w-7 h-7" /> Yes · get signature
                </button>
                <button type="button" onClick={dismissPrompt} disabled={busy} data-testid="driver-app-prompt-no"
                  className="mt-2 w-full border-2 border-gray-300 text-gray-700 py-3 font-bold uppercase mono text-sm disabled:opacity-40">
                  Not finished yet
                </button>
              </section>
            )}

            <section className="border-2 border-gray-100 p-4" data-testid="driver-app-status">
              <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Current status</div>
              <div className="heading text-2xl font-black text-[rgb(var(--primary))]" data-testid="driver-app-status-text">{statusLabel[docket.status] || docket.status}</div>
              <div className="mt-2 h-1.5 bg-gray-100"><div className="h-full bg-[rgb(var(--primary))] transition-all" style={{ width: `${Math.max(8, ((stepIdx + 1) / ORDER.length) * 100)}%` }} /></div>
            </section>

            <section className="space-y-2" data-testid="driver-app-steps">
              {steps.map((s, i) => {
                const doneStatuses = { en_route: ["en_route", "on_site", "discharging", "delivered"], on_site: ["on_site", "discharging", "delivered"], discharging: ["discharging", "delivered"], sign: ["delivered"], returning: ["delivered"] };
                const done = doneStatuses[s.key].includes(docket.status);
                const enabled = !done && s.from.includes(docket.status) && !busy;
                const Icon = s.icon;
                const onTap = () => { if (s.key === "sign") setSigning(docket); else if (s.key !== "returning") setStatus(s.key); };
                return (
                  <button key={s.key} type="button" disabled={!enabled} onClick={onTap} data-testid={`driver-app-step-${s.key}`}
                    className={`w-full flex items-center gap-4 p-4 border-2 text-left transition-colors ${done ? "border-emerald-200 bg-emerald-50" : enabled ? "border-[rgb(var(--primary))] bg-[rgb(var(--primary))]/5 active:bg-[rgb(var(--primary))]/15" : "border-gray-100 opacity-50"}`}>
                    <div className={`w-12 h-12 shrink-0 flex items-center justify-center rounded-full ${done ? "bg-emerald-500 text-white" : enabled ? "bg-[rgb(var(--primary))] text-white" : "bg-gray-200 text-gray-400"}`}>
                      {done ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                    </div>
                    <div className="min-w-0">
                      <div className="mono text-[10px] uppercase tracking-widest text-gray-400">
                        Step {i + 1}{done && String(docket[`${s.key}_source`] || "").startsWith("auto") ? " · auto by GPS" : ""}
                      </div>
                      <div className="heading text-lg font-black leading-tight">{s.label}</div>
                      <div className="text-xs text-gray-500">{done ? "Done" : enabled ? "Tap when ready" : s.sub}</div>
                    </div>
                    {!done && !enabled && <Circle className="w-4 h-4 ml-auto text-gray-300" />}
                  </button>
                );
              })}
            </section>
          </>
        )}
      </main>

      <footer className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
        <button type="button" onClick={() => nav("/driver/classic")} className="underline" data-testid="driver-app-classic">Switch to classic view</button>
        <button type="button" onClick={() => setShowSetup(true)} className="inline-flex items-center gap-1 underline mr-3" data-testid="driver-app-phone-setup"><Info className="w-3.5 h-3.5" /> Phone setup</button>
        <button type="button" onClick={async () => { await logout(); nav("/login"); }} className="inline-flex items-center gap-1 underline" data-testid="driver-app-signout"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
      </footer>

      {showPreStart && preStart.truck && (
        <PreStartModal truck={preStart.truck} onClose={() => setShowPreStart(false)} onSubmitted={() => { setShowPreStart(false); setPreStart((p) => ({ ...p, submitted: true })); toast.success("Pre-start logged"); }} />
      )}
      {water && <AddWaterDialog docket={water} onClose={() => setWater(null)} onSaved={(patch) => { setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === water.id ? { ...x, ...patch } : x)) })); setWater(null); load(); }} />}
      {signing && <SignOffDialog docket={signing} onClose={() => setSigning(null)} onSigned={() => { setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === signing.id ? { ...x, status: "delivered" } : x)) })); setSigning(null); toast.success("Docket signed — safe travels back"); setTimeout(load, 1500); }} />}
    </div>
  );
}

function SlumpCard({ docket, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const target = docket.slump_mm ?? null;
  const measured = docket.approx_slump_mm ?? null;
  const diff = target != null && measured != null ? measured - target : null;
  const off = diff != null && Math.abs(diff) > 20;
  const locked = docket.status === "delivered";
  const save = async () => {
    if (val === "" || Number.isNaN(Number(val))) return toast.error("Enter the measured slump in mm");
    setSaving(true);
    try {
      const body = { approx_slump_mm: Number(val) };
      await api.patch(`/dockets/${docket.id}/water`, body, { offline: `${docket.docket_no} · slump ${val} mm` });
      onSaved(body); setEditing(false); toast.success(`Slump ${val} mm recorded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save slump"); } finally { setSaving(false); }
  };
  return (
    <section className={`border-2 p-4 ${off ? "border-amber-500 bg-amber-50" : "border-gray-100"}`} data-testid="driver-app-slump">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Slump</div>
          <div className="text-sm text-gray-600 mt-1">Ordered: <b data-testid="driver-app-slump-target">{target != null ? `${target} mm` : "—"}</b></div>
        </div>
        <div className="text-right">
          <div className={`heading text-3xl font-black ${measured == null ? "text-gray-300" : off ? "text-amber-600" : "text-emerald-600"}`} data-testid="driver-app-slump-measured">{measured != null ? measured : "--"}<span className="text-sm ml-0.5">mm</span></div>
          <div className={`mono text-[10px] uppercase font-bold ${measured == null ? "text-gray-400" : off ? "text-amber-600" : "text-emerald-600"}`}>{measured == null ? "not checked" : off ? `${diff > 0 ? "+" : ""}${diff} mm off target` : "on target"}</div>
        </div>
      </div>
      {editing ? (
        <div className="mt-3 flex gap-2">
          <input autoFocus type="number" inputMode="numeric" step="5" placeholder={target != null ? String(target) : "mm"} value={val} onChange={(e) => setVal(e.target.value)} className="flex-1 border-2 border-gray-200 px-3 py-3 text-2xl font-black mono" data-testid="driver-app-slump-input" />
          <button type="button" onClick={save} disabled={saving} className="px-4 bg-[rgb(var(--primary))] text-white font-bold uppercase mono text-sm disabled:opacity-50" data-testid="driver-app-slump-save">Save</button>
          <button type="button" onClick={() => setEditing(false)} className="px-3 border-2 border-gray-200 font-bold uppercase mono text-sm">✕</button>
        </div>
      ) : (
        <button type="button" onClick={() => { setVal(measured ?? ""); setEditing(true); }} disabled={locked} data-testid="driver-app-slump-check" className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-gray-300 py-3 font-bold uppercase mono text-sm disabled:opacity-40">
          <Droplets className="w-4 h-4" /> {measured == null ? "Record slump check" : "Update slump"}
        </button>
      )}
    </section>
  );
}

function AddWaterDialog({ docket, onClose, onSaved }) {
  const [litres, setLitres] = useState("");
  const [slump, setSlump] = useState(docket.approx_slump_mm ?? "");
  const [saving, setSaving] = useState(false);
  const preview = useMemo(() => wcOf({ ...docket, site_water_adjust_l: Number(docket.site_water_adjust_l || 0) + Number(litres || 0) }), [docket, litres]);
  const perM3 = Number(litres || 0) / Math.max(0.1, Number(docket.quantity_m3 || 0));
  const save = async () => {
    const add = Number(litres || 0);
    if (add <= 0) return toast.error("Enter the litres added");
    if (preview.over && !window.confirm("This puts the load OVER the mix's maximum W/C ratio.\nThe supplier takes no responsibility for strength once water exceeds the limit.\n\nCustomer has accepted — continue?")) return;
    setSaving(true);
    const body = {
      slump_water_l: Number(docket.slump_water_l || 0), plant_water_l: Number(docket.plant_water_l || 0),
      site_water_adjust_l: Number(docket.site_water_adjust_l || 0) + add,
      approx_slump_mm: slump === "" ? null : Number(slump),
    };
    try {
      const res = await api.patch(`/dockets/${docket.id}/water`, body, { offline: `${docket.docket_no} · +${add} L site water` });
      if (!res.data?.queued) toast.success(`+${add} L logged · W/C ${fmt(preview.actual)}`);
      onSaved(res.data?.queued ? body : res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save water"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm bg-white text-[#0f1419] rounded-none border-2 border-gray-200" data-testid="driver-app-water-dialog">
        <DialogHeader><DialogTitle className="heading uppercase flex items-center gap-2"><Droplets className="w-5 h-5 text-[rgb(var(--primary))]" /> Add water · {docket.docket_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-2">
          {[10, 20, 40, 60].map((v) => <button key={v} type="button" onClick={() => setLitres(String(Number(litres || 0) + v))} className="border-2 border-gray-200 py-3 font-bold mono" data-testid={`driver-app-water-plus-${v}`}>+{v} L</button>)}
        </div>
        <label className="block"><div className="mono text-[10px] uppercase tracking-widest text-gray-400 mb-1">Litres added now</div>
          <input type="number" inputMode="decimal" min="0" step="5" value={litres} onChange={(e) => setLitres(e.target.value)} className="w-full border-2 border-gray-200 px-3 py-3 text-2xl font-black mono" data-testid="driver-app-water-litres" /></label>
        <label className="block"><div className="mono text-[10px] uppercase tracking-widest text-gray-400 mb-1">Slump after (mm) · optional</div>
          <input type="number" inputMode="numeric" value={slump} onChange={(e) => setSlump(e.target.value)} className="w-full border-2 border-gray-200 px-3 py-2 mono" data-testid="driver-app-water-slump" /></label>
        <div className={`border-2 p-3 ${preview.over ? "border-red-500 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="driver-app-water-preview">
          <div className="flex items-center justify-between"><span className="mono text-[10px] uppercase tracking-widest">New W/C</span><span className={`heading text-2xl font-black ${preview.over ? "text-red-600" : "text-emerald-700"}`}>{fmt(preview.actual)}</span></div>
          <div className="text-xs mt-1">{fmt(perM3, 1)} L/m³ this addition · max W/C {fmt(preview.max)} {preview.over ? "· OVER LIMIT" : "· within limit"}{perM3 > 20 ? " · exceeds AS 1379 20 L/m³" : ""}</div>
        </div>
        <button type="button" onClick={save} disabled={saving} className="w-full bg-[rgb(var(--primary))] text-white py-4 heading font-black uppercase tracking-widest disabled:opacity-50" data-testid="driver-app-water-save">{saving ? "Saving…" : "Log water"}</button>
      </DialogContent>
    </Dialog>
  );
}

function SignOffDialog({ docket, onClose, onSigned }) {
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [saving, setSaving] = useState(false);
  const wc = wcOf(docket);
  const submit = async () => {
    if (!name.trim()) return toast.error("Customer name is required");
    if (!sig) return toast.error("Customer signature is required");
    setSaving(true);
    try {
      await api.post(`/dockets/${docket.id}/sign`, { signed_by: name.trim(), signature_base64: sig }, { offline: `${docket.docket_no} · signed by ${name.trim()}` });
      onSigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save signature"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm bg-white text-[#0f1419] rounded-none border-2 border-gray-200" data-testid="driver-app-sign-dialog">
        <DialogHeader><DialogTitle className="heading uppercase flex items-center gap-2"><Flag className="w-5 h-5 text-[rgb(var(--primary))]" /> Sign off · {docket.docket_no}</DialogTitle></DialogHeader>
        <div className="text-sm text-gray-600">{docket.customer_name} · {fmt(docket.quantity_m3, 1)} m³ {docket.mix_code}{wc.added ? ` · +${fmt(wc.added, 0)} L water (W/C ${fmt(wc.actual)})` : ""}</div>
        {wc.over && <div className="border-2 border-red-500 bg-red-50 text-red-700 text-xs p-2 font-bold" data-testid="driver-app-sign-wc-warning">Water added exceeds max W/C — by signing the customer accepts responsibility for strength.</div>}
        <label className="block"><div className="mono text-[10px] uppercase tracking-widest text-gray-400 mb-1">Received by (print name)</div>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border-2 border-gray-200 px-3 py-3 text-lg" placeholder="Customer / site rep" data-testid="driver-app-sign-name" /></label>
        <div><div className="mono text-[10px] uppercase tracking-widest text-gray-400 mb-1">Signature</div><div className="border-2 border-gray-200 bg-white"><SignaturePad onChange={setSig} /></div></div>
        <button type="button" onClick={submit} disabled={saving} className="w-full bg-[rgb(var(--primary))] text-white py-4 heading font-black uppercase tracking-widest disabled:opacity-50" data-testid="driver-app-sign-save">{saving ? "Saving…" : "Complete docket"}</button>
      </DialogContent>
    </Dialog>
  );
}
